import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useAuth } from "@/providers";
import { useDomainServices } from "@/providers/DomainServices";
import { RobotoButton } from "@/shared/components/RobotoButton";
import { useUpdateUserConfig } from "@/shared/domain/config/hooks";
import * as GetUserConfig from "@/shared/domain/config/hooks/useUserConfig";
import { LayoutRecord } from "@/shared/domain/layouts";
import { layoutQueryKeys } from "@/shared/domain/layouts/hooks";

import { useBulkLayoutMutation } from "./useBulkLayoutMutation";

interface DeleteLayoutConfirmationProps {
  isOpen: boolean;
  /** The layouts to delete — one (from a row's menu) or many (from a multi-select). */
  layouts: LayoutRecord[];
  onClose: () => void;
  onExited: () => void;
  onSuccess: (deleted: LayoutRecord[]) => void;
}

export function DeleteLayoutConfirmation({
  isOpen,
  layouts,
  onClose,
  onExited,
  onSuccess,
}: DeleteLayoutConfirmationProps) {
  const deleteLayout = useDeleteLayout();
  const { run, isPending, errorMessage } = useBulkLayoutMutation(
    "Failed to delete layouts. Please try again.",
  );

  const count = layouts.length;

  const handleConfirmDelete = () => {
    run(
      layouts,
      (layout) => deleteLayout.mutateAsync(layout.layout_id),
      () => onSuccess(layouts),
    );
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      TransitionProps={{ onExited }}
      maxWidth="sm"
      fullWidth
      disableRestoreFocus
    >
      <DialogTitle>
        {count === 1
          ? `Delete "${layouts[0]?.name}"`
          : `Delete ${count} layouts`}
      </DialogTitle>
      <DialogContent>
        {count === 1 ? (
          "Are you sure you want to delete this layout? This action cannot be undone."
        ) : (
          <>
            {`Are you sure you want to delete these ${count} layouts? This action cannot be undone.`}
            <Box
              component="ul"
              sx={{ mt: 1.5, mb: 0, pl: 3, maxHeight: 200, overflowY: "auto" }}
            >
              {layouts.map((layout) => (
                <Typography
                  key={layout.layout_id}
                  component="li"
                  variant="body2"
                >
                  {layout.name}
                </Typography>
              ))}
            </Box>
          </>
        )}
        {errorMessage && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {errorMessage}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <RobotoButton
          eventName="DeleteLayoutsConfirmationCanceled"
          eventProperties={{ count }}
          onClick={onClose}
          disabled={isPending}
        >
          Cancel
        </RobotoButton>
        <RobotoButton
          eventName="DeleteLayoutsConfirmationClicked"
          eventProperties={{ count }}
          onClick={handleConfirmDelete}
          color="error"
          variant="contained"
          disabled={isPending}
          startIcon={
            isPending ? <CircularProgress size={20} color="inherit" /> : null
          }
        >
          {isPending ? "Deleting..." : "Delete"}
        </RobotoButton>
      </DialogActions>
    </Dialog>
  );
}

function useDeleteLayout() {
  const { configService, layoutService } = useDomainServices();
  const { currentOrganization, currentUser } = useAuth();
  const queryClient = useQueryClient();
  const updateUserConfig = useUpdateUserConfig();

  return useMutation({
    mutationFn: async (layoutId: string) => {
      if (currentOrganization === null) {
        return Promise.reject(new Error("CurrentOrganization is not set"));
      }
      return layoutService.deleteLayout(layoutId, currentOrganization.org_id);
    },
    onSuccess: async (_, layoutId) => {
      const defaultLayoutId = await queryClient.ensureQueryData({
        queryKey: GetUserConfig.getQueryKey(
          currentUser?.user_id,
          "user_default_layout_id",
        ),
        queryFn: () => configService.getUserConfig("user_default_layout_id"),
      });

      // Clear the default-layout config if the layout just deleted was the default.
      if (defaultLayoutId === layoutId) {
        updateUserConfig.mutate({
          key: "user_default_layout_id",
          value: "",
        });
      }

      return queryClient.invalidateQueries({
        queryKey: layoutQueryKeys.lists(),
      });
    },
  });
}
